/**
 * RecipeChatPanel Component
 * Chat with Toasty about a specific recipe
 */

import { useState, useRef, useEffect } from 'react'
import { ToastyAvatar } from '@talktoast/shared'
import { Button, Input, Loading } from '@talktoast/ui'
import { ICONS, getIconSize } from '@talktoast/shared'

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
}

interface RecipeChatPanelProps {
  recipeId: string
  recipeName: string
  onSendMessage?: (message: string, conversationHistory: ChatMessage[]) => Promise<string> // Returns assistant response
}

export default function RecipeChatPanel({
  recipeId,
  recipeName,
  onSendMessage
}: RecipeChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSend = async () => {
    if (!input.trim() || isLoading || !onSendMessage) return

    const userMessage: ChatMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString()
    }

    setMessages(prev => [...prev, userMessage])
    setInput('')
    setIsLoading(true)

    try {
      // Pass current conversation history to callback
      const response = await onSendMessage(input.trim(), messages)

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response,
        timestamp: new Date().toISOString()
      }

      setMessages(prev => [...prev, assistantMessage])
    } catch (error) {
      console.error('[RecipeChatPanel] Error:', error)
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: "Sorry, I'm having trouble responding right now. Please try again.",
        timestamp: new Date().toISOString()
      }
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setInput('') // Clear input on Escape
    }
  }

  return (
    <div className="border-t border-gray-200">
      {/* Chat Header */}
      <div className="p-4 bg-toast-gold/5 border-b border-toast-gold/20">
        <div className="flex items-start gap-3">
          <ToastyAvatar className="flex-shrink-0 w-10 h-10" alt="Toasty" />
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900 text-sm">Chat with Toasty</h4>
            <p className="text-xs text-gray-600 mt-0.5">
              Ask me anything about <span className="font-medium">{recipeName}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="p-4 space-y-3 max-h-[50vh] sm:max-h-[400px] overflow-y-auto bg-gray-50">
        {messages.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 mb-3">Ask me anything about this recipe</p>
            <div className="flex flex-wrap gap-2 justify-center max-w-xs mx-auto">
              {[
                "Can I make this vegetarian?",
                "What can I substitute?",
                "How do I make this spicier?"
              ].map((question, i) => (
                <button
                  key={i}
                  onClick={() => setInput(question)}
                  className="px-3 py-1.5 text-xs text-toast-gold-700 bg-toast-gold-50 hover:bg-toast-gold-100 rounded-full border border-toast-gold-200 transition-colors"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((message, index) => {
            // Detect error messages
            const isError = message.role === 'assistant' &&
              (message.content.startsWith("Sorry, I'm having trouble") ||
               message.content.includes('Failed to') ||
               message.content.includes('Error'))

            return (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start items-start gap-2'}`}
              >
                {message.role === 'assistant' && (
                  <ToastyAvatar className="flex-shrink-0 w-8 h-8 mt-1" alt="Toasty" />
                )}
                <div
                  className={`p-3 rounded-lg max-w-[85%] ${
                    message.role === 'user'
                      ? 'bg-toast-gold text-white'
                      : isError
                        ? 'bg-red-50 text-red-900 border-2 border-red-200'
                        : 'bg-white text-gray-900 border border-gray-200'
                  }`}
                >
                <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                <div
                  className={`text-xs mt-1 ${
                    message.role === 'user' ? 'text-white/70' : 'text-gray-400'
                  }`}
                >
                  {new Date(message.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            </div>
            )
          })
        )}

        {isLoading && (
          <div className="flex justify-start items-start gap-2">
            <ToastyAvatar animated className="flex-shrink-0 w-8 h-8 mt-1" alt="Toasty is thinking" />
            <div className="bg-white p-3 rounded-lg border border-gray-200 flex items-center gap-2">
              <Loading variant="spinner" size="sm" />
              <span className="text-xs text-gray-500 animate-pulse">
                Thinking...
              </span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-3 bg-white border-t border-gray-200">
        {!onSendMessage ? (
          <div className="text-center p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              Recipe chat will be available soon! Backend integration in progress.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value.slice(0, 500))}
                onKeyDown={handleKeyPress}
                placeholder="Ask about this recipe..."
                disabled={isLoading}
                rows={1}
                maxLength={500}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-toast-gold focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <Button
                variant="primary"
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                className="flex-shrink-0"
                aria-label="Send message to Toasty"
                title="Send message"
              >
                <ICONS.actions.send className={getIconSize('sm')} />
              </Button>
            </div>
            <div className="flex items-center justify-between text-xs px-1">
              <span className="text-gray-500">Shift+Enter for new line</span>
              <span className={input.length > 450 ? 'text-toast-gold-700 font-medium' : 'text-gray-400'}>
                {input.length}/500
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
